/* eslint-disable react/forbid-prop-types,import/prefer-default-export,import/no-unresolved,react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import MonavateCardImage from 'assets/images/cl-card-monavate.png';
import UsCardVisaImage from 'assets/images/cl-card-galileo-visa.png';
import UsCardVisaVirtualImage from 'assets/images/cl-card-galileo-visa-virtual.png';
import UsCardMastercardImage from 'assets/images/cl-card-galileo-mastercard.png';
import UsCardMastercardVirtualImage from 'assets/images/cl-card-galileo-mastercard-virtual.png';
import { VerificationState } from '../../../../../utilities/constants';
import {
  CARD_NETWORKS,
  cardProviders,
  CardState,
  getCardCapabilities,
  isConnectFinancial,
  mainCardTypes,
} from '../../../../../screens/Card/constants';
import { useUsEnv } from '../../../../../utilities/sharedHooks/useUsEnv';

export const CardOrderComponent = ({
  isBannerVisible,
  cardStatus,
  defaultNetwork,
  isOrderCardAllowed,
  isUsUser,
  userCardProvider,
  userRegionSettings,
  orderableCardTypes,
  cardType,
  orderCardHandler,
  userVerificationState,
}) => {
  const { t: translate } = useTranslation();

  const { isUsEnv } = useUsEnv();

  const { usCardArtwork } = getCardCapabilities(userCardProvider);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [bgPosition, setBgPosition] = useState(usCardArtwork ? '15% 35%' : '15% 0%');

  useEffect(() => {
    if (windowWidth < 768) {
      setBgPosition('55% 10%');
    } else {
      setBgPosition(usCardArtwork ? '15% 35%' : '15% 0%');
    }
  }, [windowWidth]);

  useEffect(() => {
    const handleWindowResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  const cardDesignImage = useMemo(() => {
    if (usCardArtwork && defaultNetwork === CARD_NETWORKS.VISA) {
      return {
        browser: `url(${UsCardVisaVirtualImage})`,
        mobile: UsCardVisaVirtualImage,
      };
    }

    if (usCardArtwork && defaultNetwork === CARD_NETWORKS.VISA) {
      return {
        browser: `url(${UsCardVisaImage})`,
        mobile: UsCardVisaImage,
      };
    }

    if (usCardArtwork && defaultNetwork === CARD_NETWORKS.MASTERCARD) {
      return {
        browser: `url(${UsCardMastercardVirtualImage})`,
        mobile: UsCardMastercardVirtualImage,
      };
    }

    if (usCardArtwork && defaultNetwork === CARD_NETWORKS.MASTERCARD) {
      return {
        browser: `url(${UsCardMastercardImage})`,
        mobile: UsCardMastercardImage,
      };
    }
    if (
      userCardProvider === cardProviders.MONAVATE ||
      (userCardProvider === cardProviders.MONAVATE &&
        userRegionSettings?.default_card_type?.toLowerCase() === mainCardTypes.VIRTUAL)
    ) {
      return {
        browser: `url(${MonavateCardImage})`,
        browserSize: '35rem',
        browserPosition: '10% 0%',
        mobile: MonavateCardImage,
      };
    }

    return {
      browser: `url(${MonavateCardImage})`,
      mobile: MonavateCardImage,
    };
  }, [userCardProvider, userRegionSettings, defaultNetwork]);

  const promoTitle = useMemo(() => {
    if (cardStatus && cardStatus?.cardState === CardState.PENDING) {
      return translate('promo_pending_card_title');
    }
    if (isUsUser) {
      return translate('promo_title_us');
    }
    return translate('promo_title');
  }, [cardStatus]);

  const promoSubtitle = useMemo(() => {
    if (cardStatus && cardStatus?.cardState === CardState.PENDING) {
      return translate(
        isConnectFinancial(userCardProvider)
          ? 'card_connect_financial_pending_subtitle'
          : 'promo_pending_card_subtitle'
      );
    }

    if (isUsUser) {
      return translate('promo_subtitle_us');
    }

    if (
      !orderableCardTypes?.includes(mainCardTypes.PHYSICAL.toUpperCase()) &&
      cardType === mainCardTypes.VIRTUAL
    ) {
      return translate('promo_virtual_subtitle');
    }

    return translate('promo_subtitle');
  }, [cardStatus, orderableCardTypes, cardType, userCardProvider]);

  const btnLabel = useMemo(() => {
    if (userVerificationState === VerificationState.UNVERIFIED) {
      return translate('verify_account_btn_label');
    }
    if (isUsUser) {
      return translate('card_order_try_now_btn_label_us');
    }

    return translate('card_order_try_now_btn_label');
  }, [isUsUser, userVerificationState]);

  const renderBanner = () => (
    <div className="bg-info d-flex flex-row w-100 align-items-center p-3 p-md-4 order-card-panner">
      <i className="icon icon-info-icon me-3 text-f5" />
      <p className="text-white text-f5">
        {!isOrderCardAllowed
          ? translate('card_verification_banner_info')
          : translate(
              isConnectFinancial(userCardProvider)
                ? 'card_connect_financial_pending_banner_info'
                : 'card_pending_banner_info'
            )}
      </p>
    </div>
  );

  const renderDisclaimer = () => (
    <div className="col-md-4 text-f7 text-shadow" style={{ marginTop: '2.5rem' }}>
      <div>{translate('us_bank_disclaimer_visa_2')}</div>
    </div>
  );

  return (
    <div className="d-flex flex-md-column p-sm-4 flex-row position-relative">
      <div
        className="bg-lighter cover-bg col-6 col-md-12 align-items-md-end align-items-start flex-md-row-center card position-relative mx-md-auto p-md-5 p-4"
        style={{
          backgroundImage: cardDesignImage?.browser,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: bgPosition,
          backgroundSize: cardDesignImage?.browserSize || '28rem',
          borderBottomRightRadius: isBannerVisible && 0,
          borderBottomLeftRadius: isBannerVisible && 0,
          minHeight: '24.375rem',
        }}>
        <div className="col-7 d-md-block d-none" />
        <div className="col-md-5 p-0 position-relative d-flex  mt-auto" style={{ zIndex: 1 }}>
          <div className="w-100 px-md-4">
            {cardStatus?.cardState !== CardState.PENDING && (
              <p className="mb-3 w-100 text-primary text-f5">{translate('promo_header')}</p>
            )}
            <h1
              className="promo-title mb-4 w-md-100 text-uppercase"
              style={{
                fontSize: cardStatus?.cardState === CardState.PENDING && '2rem',
                lineHeight: cardStatus?.cardState === CardState.PENDING && '120%',
                marginTop: cardStatus?.cardState === CardState.PENDING && '2rem',
              }}>
              {promoTitle}
            </h1>
            <p className="text-shadow m-0 mb-5 text-left w-100 d-block me-2">
              {promoSubtitle}
              {isUsUser && (
                <span className="text-shadow m-0 text-left w-100 d-block me-2">
                  {translate('promo_subtitle_us_2')}
                </span>
              )}
            </p>

            {cardStatus?.cardState !== CardState.PENDING && (
              <div className="w-100 d-flex flex-row">
                <div className="me-3 w-100">
                  <button
                    type="button"
                    className="btn btn-primary py-3 w-100"
                    disabled={!isOrderCardAllowed}
                    onClick={orderCardHandler}>
                    {btnLabel}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {isBannerVisible && renderBanner()}
      {cardStatus?.cardState !== CardState.PENDING && (
        <div className="col-6 col-md-12 pt-5 d-flex flex-md-row flex-column w-md-100 mx-auto mt-md-4 pb-5">
          <div className="col-md-4 mb-sm-4 px-4 align-items-center  d-flex flex-column justify-content-center ">
            <div
              className="rounded-circle  bg-lighter d-flex justify-content-center align-items-center mb-4"
              style={{ width: '4rem', height: '4rem' }}>
              <i
                className="icon icon-card-outline h2 d-block m-0 text-light"
                style={{ width: 'unset' }}
              />
            </div>
            <p
              className="mb-2 text-bold text-center text-f5  w-70"
              style={{ lineHeight: '1.5rem' }}>
              {translate('promo_bubble_1_title')}
            </p>
            <p className="text-shadow text-center m-0">{translate('promo_bubble_1_body')}</p>
          </div>
          <div className="col-md-4 px-4 mb-sm-4 align-items-center  d-flex flex-column justify-content-center">
            <div
              className="rounded-circle bg-lighter d-flex justify-content-center align-items-center mb-4"
              style={{ width: '4rem', height: '4rem' }}>
              <i
                className="icon icon-lightning h2 d-block m-0 text-light"
                style={{ width: 'unset' }}
              />
            </div>
            <p className="mb-2 text-bold text-center text-f5 w-70">
              {translate(isUsUser ? 'promo_bubble_2_title_us' : 'promo_bubble_2_title')}
            </p>
            <p className="text-shadow text-center m-0">
              {translate(isUsUser ? 'promo_bubble_2_body_us' : 'promo_bubble_2_body')}
            </p>
          </div>
          <div className="col-md-4 px-4 mb-sm-4 align-items-center  d-flex flex-column justify-content-center">
            <div
              className="rounded-circle bg-lighter d-flex justify-content-center align-items-center mb-4"
              style={{ width: '4rem', height: '4rem' }}>
              <img src="./assets/icons/cashback.svg" alt="Cashback" className="pt-1" />
            </div>
            <p className="mb-2 text-center text-bold text-f5 w-70">
              {translate(isUsEnv ? 'promo_bubble_3_us_title' : 'promo_bubble_3_title')}
            </p>
            <p className="text-shadow text-center m-0">
              {translate(isUsEnv ? 'promo_bubble_3_us_body' : 'promo_bubble_3_body')}
            </p>
          </div>
        </div>
      )}
      {isUsUser && userCardProvider === cardProviders.GALILEO ? renderDisclaimer() : <></>}
    </div>
  );
};

CardOrderComponent.propTypes = {
  orderCardHandler: PropTypes.func.isRequired,
  userVerificationState: PropTypes.number.isRequired,
  isBannerVisible: PropTypes.bool.isRequired,
  cardStatus: PropTypes.object.isRequired,
  defaultNetwork: PropTypes.string.isRequired,
  isOrderCardAllowed: PropTypes.bool.isRequired,
  isUsUser: PropTypes.bool.isRequired,
  userCardProvider: PropTypes.string.isRequired,
  userRegionSettings: PropTypes.object.isRequired,
  orderableCardTypes: PropTypes.array.isRequired,
  cardType: PropTypes.string.isRequired,
};
